import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import TrainingPdfViewer from './TrainingPdfViewer';

const FIELD_TYPES = [
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'signature', label: 'Signature' },
  { value: 'initial', label: 'Initial' },
  { value: 'date', label: 'Date' },
];

const SIGNER_ROLES = [
  { value: 'employee', label: 'Employee' },
  { value: 'supervisor', label: 'Supervisor' },
];

const fieldTypeLabel = (type) => FIELD_TYPES.find((t) => t.value === type)?.label || type;
const roleLabel = (role) => SIGNER_ROLES.find((r) => r.value === role)?.label || 'Employee';

const TrainingFieldPlacementEditor = ({ pdfBlob, fields, onChange, onSave, saving }) => {
  const [placementType, setPlacementType] = useState('checkbox');
  const [placementRole, setPlacementRole] = useState('employee');
  const [localFields, setLocalFields] = useState(fields || []);

  useEffect(() => {
    setLocalFields(fields || []);
  }, [fields]);

  const handlePlaceField = (field) => {
    const next = [
      ...localFields,
      {
        ...field,
        signer_role: placementRole,
        label: `${roleLabel(placementRole)} ${fieldTypeLabel(field.field_type)} (page ${field.page_number})`,
        sort_order: localFields.length,
      },
    ];
    setLocalFields(next);
    onChange?.(next);
  };

  const removeField = (index) => {
    const next = localFields.filter((_, i) => i !== index);
    setLocalFields(next);
    onChange?.(next);
  };

  if (!pdfBlob) {
    return <Alert severity="info">Load the PDF to place interactive fields.</Alert>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose who fills the field and its type, then click on the PDF to place it.
        Employee fields are filled during training; supervisor fields are filled at sign-off.
        Use page navigation for multi-page documents.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filled by</InputLabel>
          <Select
            value={placementRole}
            label="Filled by"
            onChange={(e) => setPlacementRole(e.target.value)}
          >
            {SIGNER_ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Field type</InputLabel>
          <Select
            value={placementType}
            label="Field type"
            onChange={(e) => setPlacementType(e.target.value)}
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={`${localFields.length} field${localFields.length === 1 ? '' : 's'} placed`} size="small" />
        <Button variant="contained" size="small" disabled={saving} onClick={() => onSave?.(localFields)}>
          {saving ? 'Saving…' : 'Save PDF fields'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <TrainingPdfViewer
            pdfBlob={pdfBlob}
            fields={localFields}
            placementMode
            placementType={placementType}
            onPlaceField={handlePlaceField}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Placed fields</Typography>
          {localFields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No fields yet.</Typography>
          ) : (
            <List dense disablePadding>
              {localFields.map((field, idx) => (
                <ListItem key={idx} divider sx={{ px: 0 }}>
                  <ListItemText
                    primary={field.label || fieldTypeLabel(field.field_type)}
                    secondary={`Page ${field.page_number} · ${roleLabel(field.signer_role)} · ${fieldTypeLabel(field.field_type)}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => removeField(idx)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TrainingFieldPlacementEditor;
